import { FC, useEffect, useMemo, useRef, useState } from "react";
import { Theme } from "../../../types";
import { MenuProps } from "../../types";
import clsx from "clsx";
import { useTranslations } from "use-intl";
import { useStoreState } from "../../store/useStore";
import useFetch from "../../../hooks/useFetch";
import { clipImage } from "../../utils";
import {
  megaMenuGroupTitle,
  megaMenuHoverRow,
  megaMenuLinkTitle,
  megaMenuItemDesc,
  megaMenuSkeletonBg,
  megaMenuLabel60,
  stylesText,
  dropDownStylesDesText
} from "../../styles";
import TopRoi1Bg from "@/nav-localized/assets/header/nav/top-roi1.svg?react";
import TopRoi2Bg from "@/nav-localized/assets/header/nav/top-roi2.svg?react";
import TopRoi3Bg from "@/nav-localized/assets/header/nav/top-roi3.svg?react";

// ─── Types ────────────────────────────────────────────────────

interface LeaderboardWidgetProps {
  theme: Theme;
  /** 当前 hover 的 isPrimary 项，用于区分 futures / spot 上下文 */
  hoveredItem?: MenuProps | null;
}

interface TraderItem {
  /** 交易者昵称 */
  nickName: string;
  /** 头像 URL */
  avatar: string;
  /** 30D ROI（已格式化百分比字符串，如 "+20.34%"） */
  roi30d: string;
  /** 跳转链接 */
  url: string;
  /** 排名徽章图片 URL（可选） */
  rankBadge?: string;
}

// ─── Bottom Links (Leaderboard & All Traders) ─────────────────

interface BottomLink {
  labelKey: string;
  descKey: string;
  url: string;
  fallbackLabel: string;
  fallbackDesc: string;
}

interface BottomLinkConfig extends BottomLink {
  /** 仅在指定 module 下显示，不填则都显示 */
  onlyModule?: "futures" | "spot";
}

const BOTTOM_LINKS: BottomLinkConfig[] = [
  {
    labelKey: "allTraders",
    descKey: "allTradersDes",
    url: "copy-trade?tab=allTraders",
    fallbackLabel: "All Traders",
    fallbackDesc: "Explore lead traders. Analyze performance. Copy with confidence"
  },
  {
    labelKey: "leaderBoard",
    descKey: "leaderBoardDes",
    url: "copy-trade?tab=leaderboard",
    fallbackLabel: "Leaderboard",
    fallbackDesc: "Track the top traders and stay ahead in crypto",
    onlyModule: "futures"
  }
];

// ─── API Types ────────────────────────────────────────────────

interface ApiResponse<T> {
  code: number;
  data: T;
}

/** Futures: /uapi/v1/copy/trader/rank 响应 */
interface FuturesRankResponse {
  top_roi_list?: FuturesTraderRaw[];
  [key: string]: any;
}

interface FuturesTraderRaw {
  uid: number;
  nick_name: string;
  profile: string;
  roi: string;
  roi_d30: string;
  followers: number;
  followers_max: number;
  follow_state: number;
  [key: string]: any;
}

/** Spot: /sapi/v1/spot_copy/trader/list 响应 */
interface SpotTraderListResponse {
  trader_info: SpotTraderRaw[];
  page_num: number;
  page_total: number;
}

interface SpotTraderRaw {
  uid: number;
  nick_name: string;
  profile: string;
  roi: string;
  roi_d30: string;
  followers: number;
  followers_max: number;
  follow_state: number;
  [key: string]: any;
}

// ─── Helpers ──────────────────────────────────────────────────

/**
 * 安全获取翻译值，如果返回的是原始 key path（含 "."），降级为 fallback
 */
const safeT = (
  t: ReturnType<typeof useTranslations<"header">>,
  key: string,
  fallback: string
): string => {
  try {
    const val = String(t(key as any) || "");
    return val && val.length > 0 ? val : fallback;
  } catch {
    return fallback;
  }
};

/**
 * 格式化 ROI 百分比
 * 输入: "0.2034" → 输出: "+20.34%"
 * 输入: "-0.05" → 输出: "-5.00%"
 */
const formatROI = (roi: string | number | undefined): string => {
  if (roi === undefined || roi === null || roi === "") return "+0.00%";
  const num = typeof roi === "string" ? parseFloat(roi) : roi;
  if (isNaN(num)) return "+0.00%";
  const pct = num * 100;
  const sign = pct >= 0 ? "+" : "";
  return `${sign}${pct.toFixed(2)}%`;
};

/**
 * 截断过长的昵称
 */
const truncateName = (name: string, maxLen = 16): string => {
  if (!name) return "Trader";
  if (name.length <= maxLen) return name;
  return name.slice(0, maxLen) + "...";
};

// ─── Main Component ───────────────────────────────────────────

/**
 * LeaderboardWidget — Copy Trading 排行榜 widget
 *
 * 匹配 Figma 设计稿 13780:2050：
 * - "Top ROI" 标题
 * - 3 位交易者排行（头像 + 昵称 + 30D ROI）
 * - "Leaderboard" 导航链接
 * - "All Traders" 导航链接
 */
const LeaderboardWidget: FC<LeaderboardWidgetProps> = ({ theme, hoveredItem }) => {
  const t = useTranslations("header");
  const { localeLink, baseUrl } = useStoreState();
  const { post } = useFetch(baseUrl);
  // 用 ref 存储 post，避免每次渲染新引用导致 useEffect 无限循环
  const postRef = useRef(post);
  postRef.current = post;

  // 判断上下文：futures / spot
  const module = useMemo(() => {
    if (!hoveredItem?.label) return "futures";
    return hoveredItem.label === "spotCopyTrading" ? "spot" : "futures";
  }, [hoveredItem?.label]);

  // ── 数据获取：Top ROI 排行榜 ──
  const [traders, setTraders] = useState<TraderItem[]>([]);
  const [loading, setLoading] = useState(true);
  const hasTopRoiData = traders.length > 0;

  // 仅在 module 切换时重新请求
  useEffect(() => {
    let cancelled = false;

    const fetchData = async () => {
      setLoading(true);
      try {
        let result: TraderItem[] = [];

        if (module === "spot") {
          // Spot: POST /sapi/v1/spot_copy/trader/list
          const res = await postRef.current<ApiResponse<SpotTraderListResponse>>(
            "sapi/v1/spot_copy/trader/list",
            {
              range_time: "2", // 30 天
              sort_field: "roi",
              sort_order: "DESC",
              hide_full_portfolios: 0,
              page_num: 1,
              page_size: 3
            }
          );
          if (res.code === 200 && res.data?.trader_info?.length) {
            result = res.data.trader_info.slice(0, 3).map((item) => ({
              nickName: truncateName(item?.nick_name || "Trader"),
              avatar: item?.profile || "",
              roi30d: formatROI(item?.roi),
              url: `copy-trade/details/${item?.uid}?module=spot`
            }));
          }
        } else {
          // Futures: POST /uapi/v1/copy/trader/rank → top_roi_list
          const res = await postRef.current<ApiResponse<FuturesRankResponse>>(
            "uapi/v1/copy/trader/rank",
            { limit: 3 },
            {
              headers: {
                "copy-trading-user": "1"
              }
            }
          );
          if (res.code === 200 && res.data?.top_roi_list?.length) {
            result = res.data.top_roi_list.slice(0, 3).map((item) => ({
              nickName: truncateName(item?.nick_name || "Trader"),
              avatar: item?.profile || "",
              roi30d: formatROI(item?.roi),
              url: `copy-trade/details/${item?.uid}?module=futures`
            }));
          }
        }

        if (!cancelled) {
          setTraders(result);
        }
      } catch {
        if (!cancelled) {
          setTraders([]);
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    fetchData();

    return () => {
      cancelled = true;
    };
  }, [module]);

  const handleNavigate = (url: string) => {
    window.location.href = `${localeLink}/${url}`;
  };

  return (
    <div className="flex flex-col w-full h-full">
      {/* ── Top ROI 标题 + Top 3 Traders（无数据时整体不展示） ── */}
      {(loading || hasTopRoiData) && (
        <>
          <div className={megaMenuGroupTitle({ mode: theme })}>{safeT(t, "topROI", "Top ROI")}</div>

          {loading ? (
            <TraderSkeleton theme={theme} />
          ) : (
            traders.map((trader, index) => (
              <TraderRow
                key={`${trader.nickName}-${index}`}
                trader={trader}
                rank={(index + 1) as 1 | 2 | 3}
                theme={theme}
                onClick={() => handleNavigate(trader.url)}
                t={t}
              />
            ))
          )}
        </>
      )}

      {/* ── 底部导航链接（Spot 只显示 All Traders） ── */}
      {BOTTOM_LINKS.filter((link) => !link.onlyModule || link.onlyModule === module).map((link) => {
        const linkUrl = `${link.url}${link.url.includes("?") ? "&" : "?"}module=${module}`;
        return (
          <a
            key={link.labelKey}
            href={`${localeLink}/${linkUrl}`}
            onClick={(e) => {
              e.preventDefault();
              handleNavigate(linkUrl);
            }}
          >
            <div className={clsx("group flex items-center", megaMenuHoverRow({ mode: theme }))}>
              <div className="flex flex-col flex-1 min-w-0">
                <span
                  className={clsx(
                    "text-[14px] leading-[20px] font-medium",
                    megaMenuLinkTitle({ mode: theme })
                  )}
                >
                  {safeT(t, link.labelKey, link.fallbackLabel)}
                </span>
                <span className={megaMenuItemDesc({ mode: theme })}>
                  {safeT(t, link.descKey, link.fallbackDesc)}
                </span>
              </div>
            </div>
          </a>
        );
      })}
    </div>
  );
};

// ─── Trader Row ──────────────────────────────────────────────

const TraderRow: FC<{
  t: ReturnType<typeof useTranslations<"header">>;
  trader: TraderItem;
  rank: 1 | 2 | 3;
  theme: Theme;
  onClick: () => void;
}> = ({ t, trader, rank, theme, onClick }) => {
  const isPositive = !trader.roi30d.startsWith("-");
  const RankBg = rank === 1 ? TopRoi1Bg : rank === 2 ? TopRoi2Bg : TopRoi3Bg;
  const rankText = String(rank).padStart(2, "0");
  const rankTextColor = rank === 1 ? "#92691D" : rank === 2 ? "#46556F" : "#835131";
  const ringGradient =
    rank === 1
      ? "linear-gradient(135deg, #F9D34A, #F2A512)"
      : rank === 2
        ? "linear-gradient(135deg, #CAD4E4, #7D99C5)"
        : "linear-gradient(135deg, #F4C7A6, #A3643C)";

  return (
    <div
      className={clsx("flex items-center gap-[4px]", megaMenuHoverRow({ mode: theme }))}
      onClick={onClick}
    >
      {/* 左侧：头像 + 昵称 */}
      <div className="flex flex-1 items-center gap-[8px] min-w-0">
        {/* Avatar */}
        <div className="relative shrink-0 w-[32px] h-[34px]">
          {/* Rank badge（左上角） */}
          <div
            className="absolute top-[1px] left-[1px] w-[12px] h-[11px] z-[1] pointer-events-none select-none"
            aria-hidden="true"
          >
            <RankBg className="absolute inset-0 block w-full h-full" />
            <span
              className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 text-[7px] font-semibold leading-[7px]"
              style={{ color: rankTextColor }}
            >
              {rankText}
            </span>
          </div>

          <div
            className={clsx("absolute left-0 top-[2px] w-[32px] h-[32px] rounded-full p-[1px]")}
            style={{ background: ringGradient }}
          >
            <div
              className={clsx(
                "w-full h-full rounded-full overflow-hidden",
                megaMenuSkeletonBg({ mode: theme })
              )}
            >
              {trader.avatar ? (
                <img
                  src={clipImage(trader.avatar, 64, 64)}
                  alt={trader.nickName}
                  className="block w-full h-full object-cover"
                  loading="lazy"
                  decoding="async"
                />
              ) : (
                <div
                  className={clsx(
                    "w-full h-full flex items-center justify-center text-[12px]",
                    dropDownStylesDesText({ mode: theme })
                  )}
                >
                  {trader.nickName.charAt(0).toUpperCase()}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* 昵称 */}
        <span
          dir="ltr"
          className={clsx(
            "text-[12px] font-medium leading-[18px] truncate",
            stylesText({ mode: theme })
          )}
        >
          {trader.nickName}
        </span>
      </div>

      {/* 右侧：30D ROI label + 百分比 */}
      <div className="flex items-center gap-[8px] shrink-0">
        <span
          className={clsx(
            "text-[12px] leading-[18px] whitespace-nowrap",
            megaMenuLabel60({ mode: theme })
          )}
        >
          {t("roi30d")}
        </span>
        <span
          dir="ltr"
          className={clsx(
            "text-[12px] font-medium leading-[18px] whitespace-nowrap",
            isPositive ? "text-light-green" : "text-light-red"
          )}
        >
          {trader.roi30d}
        </span>
      </div>
    </div>
  );
};

// ─── Trader Skeleton ─────────────────────────────────────────

const TraderSkeleton: FC<{ theme: Theme }> = ({ theme }) => {
  const bg = megaMenuSkeletonBg({ mode: theme });

  return (
    <>
      {[1, 2, 3].map((i) => (
        <div key={i} className="flex items-center gap-[4px] p-[12px] rounded-[12px]">
          <div className="flex flex-1 items-center gap-[8px] min-w-0">
            {/* Avatar group height should match real row (34px) */}
            <div className="relative shrink-0 w-[32px] h-[34px]">
              <div className={clsx("absolute top-0 left-0 w-[12px] h-[11px] rounded-[2px]", bg)} />
              <div
                className={clsx(
                  "absolute left-0 top-[2px] w-[32px] h-[32px] rounded-full animate-pulse",
                  bg
                )}
              />
            </div>

            {/* Name bar – slightly thicker */}
            <div className={clsx("w-[96px] h-[16px] rounded-[6px] animate-pulse", bg)} />
          </div>
          <div className="flex items-center gap-[8px]">
            {/* "30D ROI" + value bars – match 18px line height, a bit thicker */}
            <div className={clsx("w-[48px] h-[16px] rounded-[6px] animate-pulse", bg)} />
            <div className={clsx("w-[64px] h-[16px] rounded-[6px] animate-pulse", bg)} />
          </div>
        </div>
      ))}
    </>
  );
};

export default LeaderboardWidget;
